/**
 * @fileoverview UI Store
 * @description Zustand store for UI state management
 * @module @xtream/frontend/store/ui
 *
 * Features:
 * - Sidebar state
 * - Toast notifications
 * - Modal management
 * - Theme settings
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Toast notification type
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info';

/**
 * Toast notification
 */
export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  title: string | undefined;
  duration: number | undefined;
  createdAt: number;
}

/**
 * Add toast options
 */
export interface AddToastOptions {
  type: ToastType;
  message: string;
  title?: string | undefined;
  duration?: number | undefined;
}

/**
 * Modal configuration
 */
export interface ModalConfig {
  id: string;
  isOpen: boolean;
  data: Record<string, unknown> | undefined;
}

/**
 * Theme type - Only dark mode is supported
 */
export type Theme = 'dark';

/**
 * UI store state
 */
interface UIState {
  /** Sidebar collapsed state */
  sidebarCollapsed: boolean;
  /** Sidebar open on mobile */
  sidebarMobileOpen: boolean;
  /** Active toasts */
  toasts: Toast[];
  /** Open modals */
  modals: Record<string, ModalConfig>;
  /** Current theme */
  theme: Theme;
  /** Resolved theme (always dark) */
  resolvedTheme: 'dark';
  /** Global loading indicator */
  globalLoading: boolean;
  /** Global loading message */
  globalLoadingMessage: string | null;
}

/**
 * UI store actions
 */
interface UIActions {
  /** Toggle sidebar collapsed state */
  toggleSidebar: () => void;
  /** Set sidebar collapsed state */
  setSidebarCollapsed: (collapsed: boolean) => void;
  /** Toggle mobile sidebar */
  toggleMobileSidebar: () => void;
  /** Set mobile sidebar open state */
  setMobileSidebarOpen: (open: boolean) => void;
  /** Add a toast notification */
  addToast: (options: AddToastOptions) => string;
  /** Remove a toast by ID */
  removeToast: (id: string) => void;
  /** Clear all toasts */
  clearToasts: () => void;
  /** Open a modal */
  openModal: (id: string, data?: Record<string, unknown>) => void;
  /** Close a modal */
  closeModal: (id: string) => void;
  /** Close all modals */
  closeAllModals: () => void;
  /** Check if modal is open */
  isModalOpen: (id: string) => boolean;
  /** Get modal data */
  getModalData: <T = Record<string, unknown>>(id: string) => T | undefined;
  /** Set theme (only dark is supported) */
  setTheme: (theme: Theme) => void;
  /** Set global loading */
  setGlobalLoading: (loading: boolean, message?: string) => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Generate unique ID
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Resolve theme - always returns dark (only mode supported)
 */
function resolveTheme(_theme: Theme): 'dark' {
  return 'dark';
}

// ═══════════════════════════════════════════════════════════════════════════
// INITIAL STATE
// ═══════════════════════════════════════════════════════════════════════════

const initialState: UIState = {
  sidebarCollapsed: false,
  sidebarMobileOpen: false,
  toasts: [],
  modals: {},
  theme: 'dark',
  resolvedTheme: 'dark',
  globalLoading: false,
  globalLoadingMessage: null,
};

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

const DEFAULT_TOAST_DURATION = 5000;
const MAX_TOASTS = 5;

// ═══════════════════════════════════════════════════════════════════════════
// STORE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Zustand UI store with partial persistence
 */
export const useUIStore = create<UIState & UIActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      toggleSidebar: () => {
        set((state) => ({
          sidebarCollapsed: !state.sidebarCollapsed,
        }));
      },

      setSidebarCollapsed: (collapsed) => {
        set({ sidebarCollapsed: collapsed });
      },

      toggleMobileSidebar: () => {
        set((state) => ({
          sidebarMobileOpen: !state.sidebarMobileOpen,
        }));
      },

      setMobileSidebarOpen: (open) => {
        set({ sidebarMobileOpen: open });
      },

      addToast: (options) => {
        const id = generateId();
        const toast: Toast = {
          id,
          type: options.type,
          message: options.message,
          title: options.title,
          duration: options.duration ?? DEFAULT_TOAST_DURATION,
          createdAt: Date.now(),
        };

        set((state) => {
          // Limit max toasts
          const toasts = [toast, ...state.toasts].slice(0, MAX_TOASTS);
          return { toasts };
        });

        // Auto-remove after duration
        if (toast.duration && toast.duration > 0) {
          setTimeout(() => {
            get().removeToast(id);
          }, toast.duration);
        }

        return id;
      },

      removeToast: (id) => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        }));
      },

      clearToasts: () => {
        set({ toasts: [] });
      },

      openModal: (id, data) => {
        set((state) => {
          const newModal: ModalConfig = {
            id,
            isOpen: true,
            data: data,
          };
          return {
            modals: {
              ...state.modals,
              [id]: newModal,
            },
          };
        });
      },

      closeModal: (id) => {
        set((state) => {
          const existing = state.modals[id];
          const closedModal: ModalConfig = {
            id,
            isOpen: false,
            data: existing?.data,
          };
          return {
            modals: {
              ...state.modals,
              [id]: closedModal,
            },
          };
        });
      },

      closeAllModals: () => {
        const { modals } = get();
        const closedModals: Record<string, ModalConfig> = {};
        for (const id of Object.keys(modals)) {
          const existing = modals[id];
          if (existing) {
            closedModals[id] = {
              id: existing.id,
              isOpen: false,
              data: existing.data,
            };
          }
        }
        set({ modals: closedModals });
      },

      isModalOpen: (id) => {
        return get().modals[id]?.isOpen ?? false;
      },

      getModalData: <T = Record<string, unknown>>(id: string): T | undefined => {
        return get().modals[id]?.data as T | undefined;
      },

      setTheme: (_theme) => {
        // Only dark mode is supported
        set({ theme: 'dark', resolvedTheme: 'dark' });

        // Apply to document
        if (typeof document !== 'undefined') {
          document.documentElement.setAttribute('data-theme', 'dark');
        }
      },

      setGlobalLoading: (loading, message) => {
        set({
          globalLoading: loading,
          globalLoadingMessage: loading ? (message ?? null) : null,
        });
      },
    }),
    {
      name: 'xtream-ui-storage',
      storage: createJSONStorage(() => localStorage),
      // Only persist theme and sidebar preferences
      partialize: (state) => ({
        theme: state.theme,
        sidebarCollapsed: state.sidebarCollapsed,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Apply theme on rehydration
          const resolvedTheme = resolveTheme(state.theme);
          state.resolvedTheme = resolvedTheme;
          if (typeof document !== 'undefined') {
            document.documentElement.setAttribute('data-theme', resolvedTheme);
          }
        }
      },
    },
  ),
);

// ═══════════════════════════════════════════════════════════════════════════
// SELECTORS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Selector: Get sidebar collapsed state
 */
export const selectSidebarCollapsed = (state: UIState & UIActions) => state.sidebarCollapsed;

/**
 * Selector: Get toasts
 */
export const selectToasts = (state: UIState & UIActions) => state.toasts;

/**
 * Selector: Get theme
 */
export const selectTheme = (state: UIState & UIActions) => state.theme;

/**
 * Selector: Get resolved theme
 */
export const selectResolvedTheme = (state: UIState & UIActions) => state.resolvedTheme;

/**
 * Selector: Get global loading state
 */
export const selectGlobalLoading = (state: UIState & UIActions) => state.globalLoading;

export default useUIStore;
